import { AppHostLoadingControl } from '@office-start/app-host-ui-controls';
import { useCapabilities } from '@office-start/capabilities-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { logCopilotFunnelEvent } from '@office-start/copilot-fre-telemetry-service';
import { useStandaloneCopilotApp } from '@office-start/copilot-route-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { createGuid } from '@office-start/guid-generator';
import { useIdentity } from '@office-start/identity-service';
import { getDefaultEnResources, useLocStrings, } from '@office-start/loc-service';
import { copilotM365AppId, useAppDefinitionById, } from '@office-start/m365-apps-service';
import { showUpgradeDialog } from '@office-start/task-module-dialog-service';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppHostComponentInternal } from './AppHostComponentInternal';
import { AppHostStyledContainer, filterAppDefinitionStaticTabs, logAppHostError, logAppHostMetrics, } from './utilities';
export const AppHostComponent = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const renderRefs = useRef({
        firstRenderEnd: true,
        locStringRender: true,
    });
    const appHostSessionId = useRef(createGuid());
    useEffect(() => {
        var _a, _b, _c;
        logAppHostMetrics({
            appId: props.appId,
            scenarioName: 'AppHostComponentStart',
            clientTimeStamp: (_a = props.appbarConnectorService) === null || _a === void 0 ? void 0 : _a.userClickTime,
            tileChanged: (_b = props.appbarConnectorService) === null || _b === void 0 ? void 0 : _b.tileChanged,
            appUrl: window.location.pathname,
            appHostSessionId: appHostSessionId.current,
            resourceDeliveryType: (_c = props.appbarConnectorService) === null || _c === void 0 ? void 0 : _c.resourceDeliveryType,
        });
        //  useCallback with empty dependency array prevents rerenders. To log this scenario only once, we are using useEffect with empty dependency array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        var _a;
        if (props.appId === copilotM365AppId) {
            logCopilotFunnelEvent('onCopilotMosHubLoadStart', {
                ElapsedTime: ((_a = props.appbarConnectorService) === null || _a === void 0 ? void 0 : _a.userClickTime)
                    ? `${Date.now() -
                        props.appbarConnectorService.userClickTime}`
                    : '',
            });
        }
    }, [props.appId, props.appbarConnectorService]);
    const { data: officeComponentContext } = useOfficeComponentContext();
    const { data: officeIdentity } = useIdentity();
    const { data: fetchedLocStrings } = useLocStrings();
    const { data: capabilities } = useCapabilities();
    const { data: featureGates } = useFeatureGates();
    const isDefaultLocStringEnabled = (_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.defaultLocStringsGates) === null || _b === void 0 ? void 0 : _b.defaultLocStringsEnabled;
    const pauseSchedulerForMosApp = (_d = (_c = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _c === void 0 ? void 0 : _c.pauseSchedulerGates) === null || _d === void 0 ? void 0 : _d.pauseSchedulerEnabled;
    const intialLocStringState = isDefaultLocStringEnabled
        ? getDefaultEnResources('office-start-resources', featureGates)
        : fetchedLocStrings;
    const [locStrings, setLocStrings] = useState(intialLocStringState);
    const isStandaloneCopilotApp = useStandaloneCopilotApp();
    useEffect(() => {
        if (pauseSchedulerForMosApp) {
            props.appbarConnectorService.setPauseSchedulerForMosApp(pauseSchedulerForMosApp);
        }
    }, [pauseSchedulerForMosApp, props.appbarConnectorService]);
    useEffect(() => {
        if (fetchedLocStrings) {
            setLocStrings(fetchedLocStrings);
        }
    }, [fetchedLocStrings]);
    useEffect(() => {
        var _a, _b, _c;
        if (renderRefs.current.locStringRender && locStrings) {
            logAppHostMetrics({
                appId: props.appId,
                scenarioName: 'AppHostComponentLocStrings',
                clientTimeStamp: (_a = props.appbarConnectorService) === null || _a === void 0 ? void 0 : _a.userClickTime,
                tileChanged: (_b = props.appbarConnectorService) === null || _b === void 0 ? void 0 : _b.tileChanged,
                appUrl: window.location.pathname,
                appHostSessionId: appHostSessionId.current,
                isStandaloneApp: isStandaloneCopilotApp,
                resourceDeliveryType: (_c = props.appbarConnectorService) === null || _c === void 0 ? void 0 : _c.resourceDeliveryType,
            });
            renderRefs.current.locStringRender = false;
        }
        // To log this only when locStrings are fetched, we are using useEffect with locStrings as dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locStrings]);
    useEffect(() => {
        var _a, _b, _c;
        logAppHostMetrics({
            appId: props.appId,
            scenarioName: 'AppHostComponentAppDefinitionStart',
            clientTimeStamp: (_a = props.appbarConnectorService) === null || _a === void 0 ? void 0 : _a.userClickTime,
            tileChanged: (_b = props.appbarConnectorService) === null || _b === void 0 ? void 0 : _b.tileChanged,
            appUrl: window.location.pathname,
            appHostSessionId: appHostSessionId.current,
            isStandaloneApp: isStandaloneCopilotApp,
            resourceDeliveryType: (_c = props.appbarConnectorService) === null || _c === void 0 ? void 0 : _c.resourceDeliveryType,
        });
        // To log this only once before retrieving app definition, we are using useEffect with empty dependency array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { appDefinition: retrievedAppDefinition, missingAppDefinition } = useAppDefinitionById(props.appId, locStrings);
    useEffect(() => {
        var _a, _b, _c;
        if (retrievedAppDefinition || missingAppDefinition) {
            logAppHostMetrics({
                appId: props.appId,
                scenarioName: 'AppHostComponentAppDefinitionEnd',
                clientTimeStamp: (_a = props.appbarConnectorService) === null || _a === void 0 ? void 0 : _a.userClickTime,
                tileChanged: (_b = props.appbarConnectorService) === null || _b === void 0 ? void 0 : _b.tileChanged,
                appUrl: window.location.pathname,
                appHostSessionId: appHostSessionId.current,
                isStandaloneApp: isStandaloneCopilotApp,
                resourceDeliveryType: (_c = props.appbarConnectorService) === null || _c === void 0 ? void 0 : _c.resourceDeliveryType,
            });
        }
        // To log this only once after retrieving app definition, we are using useEffect with limited dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingAppDefinition, retrievedAppDefinition]);
    const acquisitionState = useMemo(() => retrievedAppDefinition && retrievedAppDefinition.acquisitionState, [retrievedAppDefinition]);
    const appDefinition = React.useMemo(() => filterAppDefinitionStaticTabs(retrievedAppDefinition), [retrievedAppDefinition]);
    useEffect(() => {
        if (acquisitionState === 'NeedsConsentForUpgrade' && appDefinition) {
            showUpgradeDialog(appDefinition.id);
        }
    }, [acquisitionState, appDefinition]);
    if (!!capabilities && !(capabilities === null || capabilities === void 0 ? void 0 : capabilities.m365AppsEnabled)) {
        logAppHostError('M365Apps_Capability_Disabled');
        return null;
    }
    if (!capabilities ||
        !locStrings ||
        !officeComponentContext ||
        !officeIdentity ||
        (!missingAppDefinition && !appDefinition)) {
        return (React.createElement(AppHostStyledContainer, null, (locStrings === null || locStrings === void 0 ? void 0 : locStrings.metaOSHubResources) && (React.createElement(AppHostLoadingControl, { label: locStrings.metaOSHubResources.loadingLabel }))));
    }
    if (!appDefinition) {
        logAppHostError('Invalid_App_Id');
        return (React.createElement(AppHostStyledContainer, null, locStrings.metaOSHubResources.unableToResolveAppDefinition));
    }
    // If no static tabs are configured, error
    // TODO: Support gallery tabs?
    if (!appDefinition.staticTabs || appDefinition.staticTabs.length === 0) {
        logAppHostError('Missing_Tab_Definition', 'M365_Apps_Tab_Definition_Error');
        return (React.createElement(AppHostStyledContainer, null, locStrings.metaOSHubResources.appHostErrorNoTabs));
    }
    if (renderRefs.current.firstRenderEnd) {
        logAppHostMetrics({
            appId: props.appId,
            scenarioName: 'AppHostComponentEnd',
            clientTimeStamp: (_e = props.appbarConnectorService) === null || _e === void 0 ? void 0 : _e.userClickTime,
            tileChanged: (_f = props.appbarConnectorService) === null || _f === void 0 ? void 0 : _f.tileChanged,
            appUrl: window.location.pathname,
            appHostSessionId: appHostSessionId.current,
            isStandaloneApp: isStandaloneCopilotApp,
            resourceDeliveryType: (_g = props.appbarConnectorService) === null || _g === void 0 ? void 0 : _g.resourceDeliveryType,
        });
        renderRefs.current.firstRenderEnd = false;
    }
    return (React.createElement(AppHostStyledContainer, null,
        React.createElement(AppHostComponentInternal, { appbarConnectorService: props.appbarConnectorService, appDefinition: appDefinition, appHostSessionId: appHostSessionId.current, appId: props.appId, capabilities: capabilities, entityId: props.entityId, officeComponentContext: officeComponentContext, officeIdentity: officeIdentity, subPageId: props.subPageId, locStrings: locStrings, additionalParams: props.additionalParams })));
};
//# sourceMappingURL=AppHostComponent.js.map